import { GenericAbortSignal } from 'axios';

import { Paginated } from 'models/Paginated';
import { WorkflowLookup, WorkflowLookupItem, WorkflowLookupRegistryEntry } from 'models/WorkflowLookup';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export type AdminWorkflowLookupsIndexParams = BaseIndexQueryParams & {
  clientId?: string;
  // Narrows to one list by its key, which is how the editor fetches a parent list's entries.
  key?: string;
  // Neither is a column: which family a list belongs to, and which workflow it names, are facts the
  // API resolves against its registry of keys.
  family?: string;
  workflowApiName?: string;
};

// What the caller may set on an item. Everything else it carries — whether it is customized, and
// what the code declares — is derived or written by publish, and the API drops it.
export type WorkflowLookupItemInput = {
  apiName: string;
  displayName: string;
  description?: string | null;
  archived?: boolean;
  [field: string]: unknown;
};

export const adminWorkflowLookupsQueryKeys = {
  base: ['adminWorkflowLookups'],
  index: (params: Record<string, any> = {}) => ['adminWorkflowLookups', 'index', params],
  show: (id: string) => ['adminWorkflowLookups', 'show', id],
  registry: () => ['adminWorkflowLookups', 'registry'],
};

export async function indexAdminWorkflowLookups(params: AdminWorkflowLookupsIndexParams, signal?: GenericAbortSignal) {
  return http
    .get<Paginated<WorkflowLookup>>('/admin/workflow_lookups', { params, signal })
    .then((response) => response.data);
}

export async function showAdminWorkflowLookup(id: string, signal?: GenericAbortSignal) {
  return http.get<WorkflowLookup>(`/admin/workflow_lookups/${id}`, { signal }).then((response) => response.data);
}

// Which lists exist and what fields their items carry. The keys come from the API, never from
// typed input, so the screen offers exactly what the code asks for.
export async function registryAdminWorkflowLookups(signal?: GenericAbortSignal) {
  return http
    .get<{ data: WorkflowLookupRegistryEntry[] }>('/admin/workflow_lookups/registry', { signal })
    .then((response) => response.data.data);
}

// The workflows that have display-text lists, for the list page's Workflow filter. Derived from the
// registry rather than served separately: the registry already knows which workflows are published
// and what each one's lists are called.
//
// Shaped like a paginated index because the filter dropdown loads its options through the same
// hook the client filter uses. Options passed statically are read once at mount, so a list that
// arrives after that never appears.
export async function indexAdminWorkflowLookupWorkflows(_params?: unknown, signal?: GenericAbortSignal) {
  const entries = await registryAdminWorkflowLookups(signal);
  const workflows = entries
    .filter((entry) => entry.workflowApiName)
    .reduce<{ id: string; name: string }[]>((options, entry) => {
      if (options.some((option) => option.id === entry.workflowApiName)) return options;
      options.push({ id: entry.workflowApiName!, name: entry.workflowLabel ?? entry.workflowApiName! });

      return options;
    }, []);

  return { data: workflows, meta: { totalPages: 1, totalRecords: workflows.length }, links: {} };
}

// Customizes a global list for one client. The copy starts from the global list's current entries,
// so nothing has to be retyped.
export async function createAdminWorkflowLookup(params: { key: string; clientId: string }) {
  return http.post<WorkflowLookup>('/admin/workflow_lookups', params).then((response) => response.data);
}

// The submitted list IS the list, in the order sent — that order is what the picker renders, so a
// reorder is just a save. Anything left out is archived rather than deleted.
export async function updateAdminWorkflowLookup(id: string, items: WorkflowLookupItemInput[]) {
  return http.patch<WorkflowLookup>(`/admin/workflow_lookups/${id}`, { items }).then((response) => response.data);
}

// Removes a client's customized list, returning that client to the global one.
export async function destroyAdminWorkflowLookup(id: string) {
  return http.delete(`/admin/workflow_lookups/${id}`);
}

export type { WorkflowLookup, WorkflowLookupItem };
